import { useContext, useEffect, useState } from 'react';
import { Pause, PhoneIncoming, PhoneOff, Repeat } from 'lucide-react';
import { VoiceContext } from './GlobalVoiceProvider';
import { formatPhoneNumber } from '@/lib/utils';

/**
 * Call waiting: the second call ringing over a live one ("Hold & answer" /
 * "Decline"), or the call on hold once both are up ("Swap").
 *
 * `inOverlay`: the in-call overlay renders this itself and registers, so the
 * provider's floating copy stays hidden while the overlay is on screen.
 */
export function CallWaitingBanner({ inOverlay = false }: { inOverlay?: boolean }) {
  const voice = useContext(VoiceContext);
  const [busy, setBusy] = useState(false);
  const register = voice?.registerCallWaitingBanner;

  useEffect(() => {
    if (!inOverlay || !register) return;
    return register();
  }, [inOverlay, register]);

  const waiting = voice?.waitingCall;
  if (!voice || !waiting) return null;

  const who = waiting.callerName || formatPhoneNumber(waiting.number) || 'Unknown caller';
  const ringing = waiting.status === 'ringing';
  const run = (fn: () => unknown) => async () => {
    if (busy) return;
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`w-full rounded-xl border px-3 py-2.5 flex items-center gap-3 shadow-lg ${
        ringing ? 'bg-amber-500/15 border-amber-500/30' : 'bg-muted/60 border-border'
      } ${inOverlay ? '' : 'bg-background/95 backdrop-blur'}`}
      data-testid="call-waiting-banner"
    >
      <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${ringing ? 'bg-amber-500/20' : 'bg-muted'}`}>
        {ringing ? <PhoneIncoming className="w-4 h-4 text-amber-500" /> : <Pause className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {ringing ? 'Call waiting' : 'On hold'}
        </p>
        <p className="text-sm font-medium text-foreground truncate">{who}</p>
      </div>
      {ringing ? (
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            disabled={busy}
            onClick={run(voice.declineWaiting)}
            className="h-8 px-3 rounded-full bg-red-500 text-white text-xs font-medium flex items-center gap-1 hover:bg-red-400 disabled:opacity-60"
            data-testid="button-decline-waiting"
          >
            <PhoneOff className="w-3.5 h-3.5" /> Decline
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={run(voice.holdAndAnswer)}
            className="h-8 px-3 rounded-full bg-emerald-500 text-white text-xs font-medium flex items-center gap-1 hover:bg-emerald-400 disabled:opacity-60"
            data-testid="button-hold-and-answer"
          >
            <Pause className="w-3.5 h-3.5" /> Hold &amp; answer
          </button>
        </div>
      ) : voice.canSwap ? (
        <button
          type="button"
          disabled={busy}
          onClick={run(voice.swapCalls)}
          className="h-8 px-3 rounded-full bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1 hover:opacity-90 disabled:opacity-60 shrink-0"
          data-testid="button-swap-calls"
        >
          <Repeat className="w-3.5 h-3.5" /> Swap
        </button>
      ) : null}
    </div>
  );
}
